import { ChatMessage, UserProfile } from '../types';

export interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  profile: UserProfile;
  messages: ChatMessage[];
}

const STORAGE_KEY_SESSIONS = 'fissal_chat_sessions_v1';
const STORAGE_KEY_ACTIVE_PREFIX = 'fissal_active_session_';
const STORAGE_KEY_DRAFT_PREFIX = 'fissal_draft_input_';

export function getDefaultWelcomeMessage(profile: UserProfile): ChatMessage {
  const isFamily = ['family', 'mom', 'trey', 'valen'].includes(profile);
  return {
    id: 'welcome',
    role: 'assistant',
    content: isFamily
      ? `*Purrs softly, twitching whiskers.* \n\nGreetings! This one is **Fissal**, standing watch over The Relay. How can this cat help with your photos, cameras, or leaving a note for Echo today?`
      : `*Mechanical purr oscillates smoothly at 42.8 Hz.* \n\n**Fissal Prime** online and fully linked to \`echodedicated\`. Infinite gear ticking steadily with zero amnesia drift. Ready for homelab commands, queries, or telemetry inspection.`,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    provider: 'openwebui-gemini-4-argon'
  };
}

function loadAllSessions(): Record<string, ChatSession> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SESSIONS);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch (err) {
    console.error('[ChatStorage] Failed to read sessions from localStorage:', err);
    return {};
  }
}

function persistAllSessions(sessions: Record<string, ChatSession>): void {
  try {
    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(sessions));
  } catch (err) {
    console.error('[ChatStorage] Failed to persist sessions to localStorage:', err);
  }
}

export function listSessions(profile: UserProfile): ChatSession[] {
  const all = loadAllSessions();
  return Object.values(all)
    .filter((s) => s.profile === profile)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

export function getActiveSessionId(profile: UserProfile): string | null {
  return localStorage.getItem(`${STORAGE_KEY_ACTIVE_PREFIX}${profile}`);
}

export function setActiveSessionId(profile: UserProfile, sessionId: string): void {
  localStorage.setItem(`${STORAGE_KEY_ACTIVE_PREFIX}${profile}`, sessionId);
}

export function createSession(
  profile: UserProfile,
  initialTitle?: string,
  initialMessage?: ChatMessage
): ChatSession {
  const all = loadAllSessions();
  const id = `session_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const now = new Date().toISOString();
  const welcome = initialMessage || getDefaultWelcomeMessage(profile);

  const session: ChatSession = {
    id,
    title: initialTitle || 'New Conversation',
    createdAt: now,
    updatedAt: now,
    profile,
    messages: [welcome]
  };

  all[id] = session;
  persistAllSessions(all);
  setActiveSessionId(profile, id);
  return session;
}

export function getActiveSession(profile: UserProfile): ChatSession {
  const all = loadAllSessions();
  const activeId = getActiveSessionId(profile);

  if (activeId && all[activeId] && all[activeId].profile === profile) {
    return all[activeId];
  }

  // Look for any existing session for this profile
  const userSessions = listSessions(profile);
  if (userSessions.length > 0) {
    setActiveSessionId(profile, userSessions[0].id);
    return userSessions[0];
  }

  // None exists, create fresh session
  return createSession(profile);
}

function deriveTitleFromMessage(content: string): string {
  const cleaned = content.replace(/[#*`_~]/g, '').trim();
  const firstLine = cleaned.split('\n')[0].trim();
  if (firstLine.length <= 32) return firstLine;
  return firstLine.substring(0, 32) + '...';
}

export function appendMessageToActiveSession(
  profile: UserProfile,
  message: ChatMessage
): ChatSession {
  const all = loadAllSessions();
  let session = getActiveSession(profile);
  const now = new Date().toISOString();

  // If session has default title and this is a user message, auto-title
  if (
    session.title === 'New Conversation' &&
    message.role === 'user' &&
    message.content.trim()
  ) {
    session.title = deriveTitleFromMessage(message.content);
  }

  // Avoid duplicates by id
  const existingIdx = session.messages.findIndex((m) => m.id === message.id);
  let updatedMessages: ChatMessage[];
  if (existingIdx >= 0) {
    updatedMessages = [...session.messages];
    updatedMessages[existingIdx] = message;
  } else {
    updatedMessages = [...session.messages, message];
  }

  session = {
    ...session,
    messages: updatedMessages,
    updatedAt: now
  };

  all[session.id] = session;
  persistAllSessions(all);
  return session;
}

export function updateSessionMessages(sessionId: string, messages: ChatMessage[]): void {
  const all = loadAllSessions();
  if (!all[sessionId]) return;

  all[sessionId] = {
    ...all[sessionId],
    messages,
    updatedAt: new Date().toISOString()
  };
  persistAllSessions(all);
}

export function deleteSession(profile: UserProfile, sessionId: string): ChatSession {
  const all = loadAllSessions();
  delete all[sessionId];
  persistAllSessions(all);

  const remaining = listSessions(profile);
  if (remaining.length > 0) {
    setActiveSessionId(profile, remaining[0].id);
    return remaining[0];
  }

  return createSession(profile);
}

export function renameSession(sessionId: string, newTitle: string): void {
  const all = loadAllSessions();
  if (all[sessionId]) {
    all[sessionId].title = newTitle.trim() || all[sessionId].title;
    all[sessionId].updatedAt = new Date().toISOString();
    persistAllSessions(all);
  }
}

export function clearAllSessions(profile: UserProfile): ChatSession {
  const all = loadAllSessions();
  for (const id of Object.keys(all)) {
    if (all[id].profile === profile) {
      delete all[id];
    }
  }
  persistAllSessions(all);
  return createSession(profile);
}

// Draft Input Persistence across app switching & tab navigation
export function saveDraftInput(profile: UserProfile, draft: string): void {
  try {
    if (!draft) {
      localStorage.removeItem(`${STORAGE_KEY_DRAFT_PREFIX}${profile}`);
    } else {
      localStorage.setItem(`${STORAGE_KEY_DRAFT_PREFIX}${profile}`, draft);
    }
  } catch {}
}

export function getDraftInput(profile: UserProfile): string {
  try {
    return localStorage.getItem(`${STORAGE_KEY_DRAFT_PREFIX}${profile}`) || '';
  } catch {
    return '';
  }
}
